import React, { FocusEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GroupWidgetPort } from '@kemu-io/kemu-core/types';
import { HolderOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { DataType } from '@kemu-io/hs-types';
import { SelectValue, LabeledValue } from 'antd/lib/select';
import styles from '../portsList.module.css';
import IconButton from '@components/form-control/iconButton/iconButton';
import StyledSelect, { Option } from '@components/form-control/styledSelect/styledSelect';
import StyledInput from '@components/form-control/styledInput/styledInput';
import useTranslation from '@hooks/useTranslation';
import { portTypeToString } from '@common/utils';

type Props = {
	port: GroupWidgetPort;
	index: number;
	isLast: boolean;
	onPortUpdated: (index: number, port: GroupWidgetPort) => void;
	onRemovePort: (index: number) => void;
	onAddPort: () => void;
	onPortNameChange: (text: string, originalEvent: FocusEvent<HTMLInputElement>, elementId?: string | number) => void;
	onPortTypeUpdated: (value: SelectValue, options: LabeledValue[], portId?: string | number) => void;
	handleNameChange: (evt: React.ChangeEvent<HTMLInputElement>, elementId?: string | number) => void;
};

const SortablePortRow = (props: Props) => {
	const {
		port,
		index,
		isLast,
		onPortUpdated,
		onRemovePort,
		onAddPort,
		onPortNameChange,
		onPortTypeUpdated,
		handleNameChange,
	} = props;

	const typeT = useTranslation('Kemu.DataType.HumanReadable');

	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: port.name });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	// Display port.label if set, otherwise fall back to port.name
	// Note: We use explicit undefined/null check instead of falsy check (port.label || port.name)
	// because empty string '' is falsy but is a valid value. When user clears the field,
	// port.label becomes '' which should be displayed (not fall back to port.name).
	// The fallback to port.name happens on blur via handlePortNameChange in portsList.tsx
	const displayValue = port.label !== undefined && port.label !== null ? port.label : (port.name || '');

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`${styles.PortRow} ${isDragging ? styles.Dragging : ''}`}
		>
			<div className={styles.DragHandle} ref={setActivatorNodeRef} {...listeners} {...attributes}>
				<HolderOutlined />
			</div>
			<StyledInput
				className={styles.PortName}
				value={displayValue}
				elementId={index}
				onChange={handleNameChange}
				onBlur={onPortNameChange}
			/>

			<StyledSelect
				size="middle"
				className={styles.PortType}
				mode='tags'
				maxTagCount={2}
				defaultValue={(port.type as DataType[]).map(type => portTypeToString(typeT, type))}
				onChange={onPortTypeUpdated}
				elementId={index}
			>
				<Option key={DataType.Anything} value={'Anything'}>{typeT('Anything', 'Anything')}</Option>
				<Option key={DataType.Number} value={'Number'}>{typeT('Number', 'Number')}</Option>
				<Option key={DataType.String} value={'String'}>{typeT('String', 'String')}</Option>
				<Option key={DataType.JsonObj} value={'JsonObj'}>{typeT('JsonObj', 'Object')}</Option>
				<Option key={DataType.ImageData} value={'ImageData'}>{typeT('ImageData', 'Image')}</Option>
				<Option key={DataType.Boolean} value={'Boolean'}>{typeT('Boolean', 'Boolean')}</Option>
				<Option key={DataType.Array} value={'Array'}>{typeT('Array', 'Array')}</Option>
				<Option key={DataType.Point} value={'Point'}>{typeT('Point', 'Point')}</Option>
				<Option key={DataType.Rect} value={'Rect'}>{typeT('Rect', 'Rect')}</Option>
				<Option key={DataType.AudioBuffer} value={'AudioBuffer'}>{typeT('AudioBuffer', 'Audio Buffer')}</Option>
				<Option key={DataType.ArrayBuffer} value={'ArrayBuffer'}>{typeT('ArrayBuffer', 'Array Buffer')}</Option>
				<Option key={DataType.BinaryFile} value={'BinaryFile'}>{typeT('BinaryFile', 'Binary File')}</Option>
			</StyledSelect>

			<div className={styles.Actions}>
				{isLast ? (
					<>
						<IconButton
							className={classNames(styles.RemovePortBtn, styles.PortActionBtn)}
							color="transparent"
							shape="square"
							onClick={() => onRemovePort(index)}
							icon={<DeleteFilled />}
						/>
						<IconButton
							className={styles.PortActionBtn}
							color="transparent"
							shape="square"
							onClick={onAddPort}
							icon={<PlusOutlined />}
						/>
					</>
				) : (
					<IconButton
						className={classNames(styles.RemovePortBtn, styles.PortActionBtn)}
						color="transparent"
						shape="square"
						onClick={() => onRemovePort(index)}
						icon={<DeleteFilled />}
					/>
				)}
			</div>
		</div>
	);
};

export default SortablePortRow;
